import { useMemo, useState } from 'react'
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { Loader2, Share2, UserPlus, X } from 'lucide-react'
import { toast } from 'sonner'
import { useTranslation } from 'react-i18next'
import { Badge } from '@/components/ui/badge'
import { Button } from '@/components/ui/button'
import { Input } from '@/components/ui/input'
import { ToggleGroup, ToggleGroupItem } from '@/components/ui/toggle-group'
import {
  getMembers,
  getUsers,
  shareCollection,
  unshareCollection,
} from '@/lib/api/auth/users'
import { apiErrorMessage } from '@/lib/api-client'
import { useAuth } from '@/components/providers/auth-provider'
import type { Collection } from '@/types/terms'

const ROLES = ['viewer', 'editor', 'owner'] as const

/**
 * Who else can reach this collection. Only an owner may open it — the backend
 * enforces that too, this just avoids showing a control that would 403.
 */
export function SharePanel({ collection }: { collection: Collection | undefined }) {
  const { t } = useTranslation()
  const qc = useQueryClient()
  const { user } = useAuth()
  const [open, setOpen] = useState(false)
  const [query, setQuery] = useState('')
  const [role, setRole] = useState('viewer')

  const canShare = collection?.my_role === 'owner' || user?.is_admin
  const cid = collection?.id

  const members = useQuery({
    queryKey: ['members', cid],
    queryFn: () => getMembers(cid!),
    enabled: !!cid && open,
  })
  const users = useQuery({ queryKey: ['users'], queryFn: getUsers, enabled: open })

  const candidates = useMemo(() => {
    const q = query.trim().toLowerCase()
    const already = new Set((members.data ?? []).map((m) => m.user_id))
    return (users.data ?? [])
      .filter((u) => !already.has(u.id))
      .filter((u) => !q || u.name.toLowerCase().includes(q) || u.username.toLowerCase().includes(q))
      .slice(0, 6)
  }, [users.data, members.data, query])

  const share = useMutation({
    mutationFn: (userId: number) => shareCollection(cid!, userId, role),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['members', cid] })
      setQuery('')
      toast.success(t('share.shared'))
    },
    onError: (e) => toast.error(t('share.shareFailed'), { description: apiErrorMessage(e) }),
  })

  const changeRole = useMutation({
    mutationFn: ({ userId, role: r }: { userId: number; role: string }) =>
      shareCollection(cid!, userId, r),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['members', cid] })
      qc.invalidateQueries({ queryKey: ['collections'] })
      toast.success(t('share.roleChanged'))
    },
    onError: (e) => toast.error(t('share.roleChangeFailed'), { description: apiErrorMessage(e) }),
  })

  const revoke = useMutation({
    mutationFn: (userId: number) => unshareCollection(cid!, userId),
    onSuccess: () => qc.invalidateQueries({ queryKey: ['members', cid] }),
    onError: (e) => toast.error(t('share.revokeFailed'), { description: apiErrorMessage(e) }),
  })

  if (!canShare || !cid) return null

  return (
    <div className="relative">
      <Button
        variant="ghost"
        size="icon-sm"
        onClick={() => setOpen((v) => !v)}
        title={t('share.button')}
      >
        <Share2 />
      </Button>

      {open && (
        <>
          <div className="fixed inset-0 z-40" onClick={() => setOpen(false)} />
          <div className="absolute left-0 top-full z-50 mt-1 w-80 rounded-md border border-border bg-card p-3 shadow-lg">
            <p className="mb-2 text-xs font-medium">
              {t('share.heading', { name: collection?.name ?? '' })}
            </p>

            <div className="mb-2 space-y-1.5">
              {members.isLoading ? (
                <Loader2 className="size-4 animate-spin text-muted-foreground" />
              ) : (
                (members.data ?? []).map((m) => (
                  <div key={m.user_id} className="flex items-center gap-2">
                    <div className="min-w-0 flex-1">
                      <p className="truncate text-[13px]">{m.name}</p>
                      <p className="truncate text-[10px] text-muted-foreground">{m.username}</p>
                    </div>
                    {m.role === 'owner' ? (
                      <Badge variant="primary">{t('roles.owner')}</Badge>
                    ) : (
                      <>
                        {/* Role is editable in place: the backend upserts, so
                            changing it needs no remove-then-re-add dance. */}
                        <select
                          value={m.role}
                          onChange={(e) => changeRole.mutate({ userId: m.user_id, role: e.target.value })}
                          disabled={changeRole.isPending}
                          className="h-6 rounded border border-border bg-card px-1 text-[11px]"
                        >
                          {ROLES.map((r) => (
                            <option key={r} value={r}>
                              {t(`roles.${r}` as const)}
                            </option>
                          ))}
                        </select>
                        <Button
                          variant="ghost"
                          size="icon-sm"
                          onClick={() => revoke.mutate(m.user_id)}
                          title={t('share.revoke')}
                        >
                          <X />
                        </Button>
                      </>
                    )}
                  </div>
                ))
              )}
            </div>

            <div className="border-t border-border pt-2">
              <ToggleGroup
                type="single"
                value={role}
                onValueChange={(v) => v && setRole(v)}
                className="mb-1.5 w-full"
              >
                {ROLES.map((r) => (
                  <ToggleGroupItem key={r} value={r} className="flex-1">
                    {t(`roles.${r}` as const)}
                  </ToggleGroupItem>
                ))}
              </ToggleGroup>

              <Input
                value={query}
                onChange={(e) => setQuery(e.target.value)}
                placeholder={t('share.searchUsers')}
                className="h-7 text-xs"
              />
              <div className="mt-1 max-h-40 overflow-y-auto scrollbar-thin">
                {candidates.map((u) => (
                  <button
                    key={u.id}
                    type="button"
                    onClick={() => share.mutate(u.id)}
                    disabled={share.isPending}
                    className="flex w-full items-center gap-2 rounded px-2 py-1 text-left text-[13px] hover:bg-muted"
                  >
                    <UserPlus className="size-3.5 shrink-0 text-muted-foreground" />
                    <span className="min-w-0 flex-1 truncate">{u.name}</span>
                    <span className="truncate text-[10px] text-muted-foreground">{u.username}</span>
                  </button>
                ))}
                {!candidates.length && (
                  <p className="px-2 py-1 text-[11px] text-muted-foreground">
                    {query ? t('common.noMatches') : t('share.allUsersAdded')}
                  </p>
                )}
              </div>
            </div>
          </div>
        </>
      )}
    </div>
  )
}
